import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import ImageStep from '../components/ImageStep/ImageStep';
import SequenceView, { animateSequence } from '../components/SequenceView/SequenceView';
import StepModal from '../components/StepModal/StepModal';
import { actionCreators, DragDirection, InventoryChangeType, SequenceInfo, SequenceType, State, StepInfo, StepType, TrainingModule } from '../state';
import { useEffect, useCallback, useState } from 'react'
import './TrainingStep.css';
import ModuleCompleted from '../components/ModuleCompleted/ModuleCompleted';
import InventoryModule from '../components/InventoryModule/InventoryModule';
import * as ImageLoader from '../image_preloader'

var image_preloader: ImageLoader.ImagePreloader

const TrainingStepPage: React.FC = () => {
    
    const { trainingState, navState } = useSelector((state: State) => state)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { updateTrainingState, updateNavState, updateInventoryTypeState, updateLoadingState } = bindActionCreators(actionCreators, dispatch)

    const stepScale = useCallback(() => {
        const scale1 = window.screen.width / 1920;
        const scale2 = window.screen.height / 1080;
        return Math.max(scale1,scale2);
    }, [])
    
    const [scale, setScale] = useState(stepScale());
    const [completed, setCompleted] = useState(false)
    const [seqInfo, setSeqInfo] = useState<SequenceInfo>()

    function getSubModuleInfo() {
        const training = trainingState.currentTraining
        let subModuleIndex = training!.currentSubModuleIndex
        let useAlternates = false
        if (subModuleIndex < 0) {
            useAlternates = true
            subModuleIndex = Math.abs((subModuleIndex + 1))
        }
        const subMod = useAlternates ? training!.alternateModules![subModuleIndex] : training?.subModules[subModuleIndex]
        return subMod
    }

    function getSteps() {
        const subMod = getSubModuleInfo()
        return subMod?.steps ?? []
    }

    function getStepInfo() {
        const training = trainingState.currentTraining
        const subModSteps = getSteps()
        const stepInfo = subModSteps[training?.currentStepIndex ?? 0]
        return stepInfo
    }

    const preloadImages = (sequenceInfo?: SequenceInfo) => {
        if (sequenceInfo) {
            if (image_preloader && image_preloader.currentPrefix === sequenceInfo.prefix) {
                return image_preloader
            }
            // console.log(`preloadImages began for seq ${sequenceInfo.prefix}`)
            image_preloader = new ImageLoader.ImagePreloader((percent: number) => {
                // console.log("sequences preloaded "+Math.ceil(percent*100)+"%")
            }, () => {
                // console.log(`preloadImages complete for seq ${sequenceInfo.prefix}`)
                updateLoadingState({loading: false})
            })
            image_preloader.addImageSequence(sequenceInfo.prefix, sequenceInfo.start, sequenceInfo.end, sequenceInfo.suffix, false)
            image_preloader.go()
            updateLoadingState({loading: true})
            return image_preloader
        }
        return undefined
    }
    const resize = () => {
        setScale(stepScale());
    }

    useEffect(() =>{
        window.addEventListener('resize', resize)
        if (document.getElementsByClassName('tappableArea')[0] && document.getElementsByClassName('tappableArea')[0].classList.contains('hidden')) {
            document.getElementsByClassName('tappableArea')[0].classList.remove('hidden')
        }
        return () => {
            window.removeEventListener('resize', resize);
        }
    })
    useEffect(() => {
        const training = trainingState.currentTraining
        let subModuleIndex = training!.currentSubModuleIndex
        let useAlternates = false
        if (subModuleIndex < 0) {
            useAlternates = true
            subModuleIndex = Math.abs((subModuleIndex + 1))
        }
        const subMod = useAlternates ? training!.alternateModules![subModuleIndex] : training?.subModules[subModuleIndex]
        if (!seqInfo || subMod?.sequenceInfo?.prefix !== seqInfo.prefix) {
            setSeqInfo((prevSeqInfo) => subMod!.sequenceInfo)
        }
        if (document.getElementsByClassName('tappableArea')[0] && document.getElementsByClassName('tappableArea')[0].classList.contains('hidden')) {
            document.getElementsByClassName('tappableArea')[0].classList.remove('hidden')
        }
        if (document.getElementsByClassName('sequence-view-tap')[0] && document.getElementsByClassName('sequence-view-tap')[0].classList.contains('hidden')) {
            document.getElementsByClassName('sequence-view-tap')[0].classList.remove('hidden')
        }
    }, [seqInfo, setSeqInfo, trainingState])
    useEffect(() => {
        preloadImages(seqInfo)
    })

    function incrementStep() {
        var ns = navState.stack
        var training = trainingState.currentTraining!
        const index = training.currentStepIndex + 1
        let useAlternates = false
        let subModuleIndex = training.currentSubModuleIndex
        if (subModuleIndex < 0) {
            useAlternates = true
            subModuleIndex = Math.abs((subModuleIndex + 1))
        }
        const subMod = useAlternates ? training.alternateModules![subModuleIndex] : training.subModules[training.currentSubModuleIndex]
        if (index < subMod.steps.length) {
            const path = '/module'
            ns.push({
                stepNumber: index,
                subModuleIndex: training.currentSubModuleIndex,
                trainingModule: training.trainingModule,
                path: path
            })
            updateNavState({stack: ns})
            training.currentStepIndex = index
            updateTrainingState({currentTraining: training})
            navigate(path)
            if (completed) {
                setCompleted(false)
            }
        } else {
            setCompleted(true)
        }
    }

    function tapLocation(stepInfo: StepInfo) {
        if (stepInfo.tapLocation) {
            return (
                <div 
                    className="sequence-view-tap" 
                    style={{ top: `${stepInfo.tapLocation.y}px`, left: `${stepInfo.tapLocation.x}px` }} 
                    onClick={() => {
                        document.getElementsByClassName('sequence-view-tap')[0].classList.add('hidden')
                        incrementStep()
                    }} 
                />
            )
        }
        return <></>
    }

    function stepContent() {
        const stepInfo = getStepInfo()
        if (stepInfo) {
            switch (stepInfo.type) {
                case StepType.Image:
                    return (
                        <div className='clipContainer'>
                            <div className='scaleContainer' style={{transform:`translateX(-50%) scale(${scale})`}}>
                                <>
                                    <ImageStep stepInfo={stepInfo} children={tapLocation(stepInfo)} />
                                    {tappableArea()}
                                </>
                            </div>
                        </div>
                    )
                case StepType.Sequence:
                    return (
                        <div className='clipContainer'>
                            <div className='scaleContainer' style={{transform:`translateX(-50%) scale(${scale})`}}>
                                <>
                                    <SequenceView 
                                        id={`${stepInfo.type}_${stepInfo.sequenceInfo!.start}_${stepInfo.sequenceInfo!.end}`} 
                                        prefix={stepInfo.sequenceInfo?.prefix ?? ''}
                                        completion={(finished: boolean) => {
                                            if (finished) {
                                                incrementStep()
                                            }
                                        }} 
                                        tapLocation={stepInfo.tapLocation}
                                        start={stepInfo.sequenceInfo?.start ?? 0} 
                                        end={stepInfo.sequenceInfo?.end ?? 0}
                                        height={1080}
                                        width={1920}
                                        suffix={stepInfo.sequenceInfo?.suffix ?? 'jpg'}
                                        framesPerSecond={stepInfo.sequenceInfo?.framesPerSecond ?? 30}
                                        sequenceType={stepInfo.sequenceInfo?.type ?? SequenceType.Progress}
                                        dragDirection={stepInfo.sequenceInfo?.dragDirection ?? DragDirection.RightToLeft}
                                        imagePreloader={preloadImages(seqInfo)}
                                        dragInfo={stepInfo.dragInfo}
                                    />
                                    {tappableArea()}
                                </>
                            </div>
                        </div>
                    )
                case StepType.Inventory:
                    return <InventoryModule didComplete={incrementStep} />
                default:
                    return <div>Step type not yet implemented</div>
            }
        } else {
            return <></>
        }
    }

    function tappableArea() {
        const training = trainingState.currentTraining
        const stepInfo = getStepInfo()
        if (stepInfo && stepInfo.tappableArea) {
            return (
                <div 
                    className={`tappableArea${stepInfo.showTappableArea ? ' highlighted' : ''}`} 
                    style={
                        { 
                            top: `${stepInfo.tappableArea.origin.y}px`, 
                            left: `${stepInfo.tappableArea.origin.x}px`,
                            width: `${stepInfo.tappableArea.size.width}px`,
                            height: `${stepInfo.tappableArea.size.height}px`
                        }
                    } 
                    onClick={(event) => {
                        if (stepInfo.type === StepType.Sequence) {
                            if (document.getElementsByClassName('sequence-view-tap')[0]) {
                                document.getElementsByClassName('sequence-view-tap')[0].classList.add('hidden')
                            }
                            document.getElementsByClassName('tappableArea')[0].classList.add('hidden')
                            animateSequence(stepInfo.sequenceInfo?.start ?? 0, stepInfo.sequenceInfo?.end ?? 0, ((finished: Boolean) => {
                                if (finished) {
                                    incrementStep()
                                }
                            }))
                        } else if (stepInfo.type === StepType.Image && training!.trainingModule === TrainingModule.Hub && training!.currentSubModuleIndex === 1) {
                            const inventoryType: InventoryChangeType = event.clientX > (window.screen.width / 2) ? InventoryChangeType.Restock : InventoryChangeType.Adjust
                            updateInventoryTypeState({type: inventoryType})
                            incrementStep()
                        } else {
                            incrementStep()
                        } 
                    }}
                />
            )
        }
    }

    function stepModal() {
        const stepInfo = getStepInfo()
        if (stepInfo && stepInfo.modalInfo) {
            return <StepModal 
                        stepInfo={stepInfo} 
                        onClick={stepInfo.modalInfo?.isInteractive ? incrementStep : ()=>{}}
                    />
        } else {
            return <></>
        }
    }

    return (
        <div className="TrainingStep">
            {trainingState.currentTraining &&
                <>
                    {stepContent()}
                    {stepModal()}
                    <div className="moduleDescription">{getSubModuleInfo()?.description ?? ''}</div>
                </>
            }
            {(completed && (trainingState.currentTraining?.currentStepIndex ?? 0) + 1 === getSteps().length) &&
                <ModuleCompleted didRestart={() => setCompleted(false)} />
            }
        </div>
    );
}

export default TrainingStepPage;